import { useEffect, useMemo, useState, type FunctionComponent } from "react";
import {
  Alert,
  DatePicker,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";

import {
  type PayoffGrantCandidatesResponse,
  type PayoffGrantOfferResponse,
  type PayoffOfferCandidate,
} from "@fitness-app/data-models/entities/Payoff";
import { type Product } from "@fitness-app/data-models/entities/Product";

import { PayoffOfferCampaignFields } from "~/modules/Products/components/PayoffOfferCampaign/PayoffOfferCampaignFields";
import {
  buildGrantOfferPayload,
  DEFAULT_BONUS_DAYS,
  DEFAULT_DISCOUNT_PCT,
  DEFAULT_FOLLOW_UP_DAYS,
  DEFAULT_OFFER_DAYS,
  followUpLabel,
  rewardLabel,
  type PayoffOfferCampaignModel,
} from "~/modules/Products/components/PayoffOfferCampaign/payoffOfferCampaign";
import { productApi } from "~/store/initializeStore";

interface OwnProps {
  product: Product;
  open: boolean;
  onClose: () => void;
}

type BulkFormModel = PayoffOfferCampaignModel;

const isOfferActive = (candidate: PayoffOfferCandidate) =>
  Boolean(
    candidate.currentOffer?.enabled &&
    !candidate.currentOffer.revokedAt &&
    candidate.currentOffer.expiresAt > Date.now(),
  );

/**
 * Bulk grant of the payoff offer (TRE-1713).
 *
 * The candidate list comes from the backend rather than the paginated client
 * table, because the price and the checkout variant of a plan live on the
 * ORDER (`product_client."lastOrder"`), not on the client row — filtering by
 * "wariant koszyka" needs that join, and the same call returns the reason for
 * every client that cannot be granted an offer.
 */
export const PayoffOfferBulkModal: FunctionComponent<OwnProps> = ({ product, open, onClose }) => {
  const [candidates, setCandidates] = useState<PayoffOfferCandidate[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [priceId, setPriceId] = useState<string | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [minRemaining, setMinRemaining] = useState<number | null>(null);
  // Clients paying by transfer qualify since TRE-1737, but they are a distinct
  // audience — no card on file, so their payoff is always entered on the payoff
  // page. Included by default (that is the point of unblocking them), and the
  // operator can run a card-only campaign by switching it off.
  const [includeManualPayments, setIncludeManualPayments] = useState(true);
  const [result, setResult] = useState<PayoffGrantOfferResponse | null>(null);
  const [formController] = Form.useForm<BulkFormModel>();
  // Campaign fields gate each other (no email → no reminder) — they need the
  // live values, not just what onFinish hands over.
  const formValues = Form.useWatch([], formController) ?? {};

  useEffect(() => {
    if (!open) {
      return;
    }

    setResult(null);
    setSelectedIds([]);
    setMinRemaining(null);
    formController.setFieldsValue({
      rewardType: "discount",
      discountPct: DEFAULT_DISCOUNT_PCT,
      bonusDays: DEFAULT_BONUS_DAYS,
      expiresAt: dayjs().add(DEFAULT_OFFER_DAYS, "day"),
      sendEmail: true,
      followUpEnabled: false,
      followUpAfterDays: DEFAULT_FOLLOW_UP_DAYS,
    });

    const load = async () => {
      setLoading(true);

      try {
        const { data } = await productApi.get<PayoffGrantCandidatesResponse>("/payoff/grant-candidates", {
          params: { productId: product.id },
        });

        setCandidates(data.candidates);
        setTruncated(data.truncated);
      } catch {
        void message.error("Nie udało się pobrać listy klientek");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [open, product.id, formController]);

  const variantOptions = useMemo(() => {
    const variants = new Map<string, string>();

    candidates.forEach((candidate) => {
      if (candidate.checkoutVariantId) {
        variants.set(candidate.checkoutVariantId, candidate.checkoutVariantSlug ?? candidate.checkoutVariantId);
      }
    });

    return [...variants].map(([value, label]) => ({ value, label }));
  }, [candidates]);

  const priceOptions = useMemo(() => {
    // Clients are counted per price so the operator can tell a live price from
    // an archived one without leaving the modal.
    const clientsPerPrice = candidates.reduce<Record<string, number>>((counters, candidate) => {
      if (candidate.priceId) {
        counters[candidate.priceId] = (counters[candidate.priceId] ?? 0) + 1;
      }

      return counters;
    }, {});

    return (product.prices ?? [])
      .filter((price) => price.installments)
      .map((price) => {
        const amount = `${((price.unit_amount ?? 0) / 100).toFixed(2)} ${price.currency.toUpperCase()} × ${price.installments} rat`;
        const clients = clientsPerPrice[price.id] ?? 0;

        return {
          value: price.id,
          // Searchable text: name, amount AND the Stripe id, since half of the
          // prices have no custom label and are known only by their id.
          label: `${price.customLabel ? `${price.customLabel} — ` : ""}${amount} · ${clients} klientek — ${price.id}`,
        };
      })
      .sort((first, second) => (clientsPerPrice[second.value] ?? 0) - (clientsPerPrice[first.value] ?? 0));
  }, [product.prices, candidates]);

  const filtered = useMemo(
    () =>
      candidates
        .filter((candidate) => (priceId ? candidate.priceId === priceId : true))
        .filter((candidate) => (variantId ? candidate.checkoutVariantId === variantId : true))
        // Optional: an offer is worth the most where there is still a lot left
        // to pay off, so the operator can cut off the tail of nearly finished
        // plans. Empty = no cut-off.
        .filter((candidate) => (minRemaining ? (candidate.remainingInstallments ?? 0) >= minRemaining : true))
        .filter((candidate) => (includeManualPayments ? true : !candidate.isManualPayment)),
    [candidates, priceId, variantId, minRemaining, includeManualPayments],
  );
  const manualCount = filtered.filter((candidate) => candidate.isManualPayment && candidate.eligible).length;

  const eligibleIds = filtered.filter((candidate) => candidate.eligible).map((candidate) => candidate.productClientId);
  // Selecting, then narrowing the filter, must not grant offers to clients the
  // operator can no longer see.
  const effectiveSelection = selectedIds.filter((id) => eligibleIds.includes(id));

  const onSubmit = async (data: BulkFormModel) => {
    if (!effectiveSelection.length) {
      void message.warning("Zaznacz co najmniej jedną klientkę");

      return;
    }

    setSaving(true);

    try {
      const { data: response } = await productApi.post<PayoffGrantOfferResponse>(
        "/payoff/grant-offer",
        buildGrantOfferPayload(data, effectiveSelection),
      );

      setResult(response);
      setSelectedIds([]);
      void message.success(`Przyznano ofertę: ${response.granted.length}`);
    } catch {
      void message.error("Nie udało się przyznać oferty");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Oferta dopłaty do całości rat — akcja masowa"
      open={open}
      width={1000}
      onCancel={onClose}
      okText={`Przyznaj ofertę (${effectiveSelection.length})`}
      onOk={() => formController.submit()}
      okButtonProps={{ loading: saving, disabled: saving || !effectiveSelection.length }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Space wrap>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 420 }}
            popupMatchSelectWidth={560}
            placeholder="Cena (raty) — szukaj po nazwie, kwocie lub id"
            options={priceOptions}
            value={priceId}
            onChange={(value) => setPriceId(value ?? null)}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 240 }}
            placeholder="Wariant koszyka"
            options={variantOptions}
            value={variantId}
            onChange={(value) => setVariantId(value ?? null)}
          />
          <InputNumber
            min={2}
            max={60}
            precision={0}
            style={{ width: 230 }}
            addonBefore="Min. rat do końca"
            placeholder="dowolna"
            value={minRemaining}
            onChange={(value) => setMinRemaining(value ?? null)}
          />
          <Tooltip title="Klientki spłacające raty przelewem (bez zapisanej karty). Dopłatę opłacają kartą lub BLIK-iem na stronie oferty.">
            <Space size={6}>
              {/* The label is a sibling, not a `<label for>` — without this the
                  control is announced unnamed. */}
              <Switch
                aria-label="Raty manualne (przelew)"
                checked={includeManualPayments}
                onChange={setIncludeManualPayments}
                size="small"
              />
              <Typography.Text>Raty manualne (przelew)</Typography.Text>
            </Space>
          </Tooltip>
          <Typography.Text type="secondary">
            {filtered.length} klientek, w tym {eligibleIds.length} kwalifikujących się
            {includeManualPayments && manualCount ? ` (${manualCount} z ratami manualnymi)` : ""}
          </Typography.Text>
        </Space>

        {truncated && (
          <Alert
            type="warning"
            showIcon
            message="Lista jest ucięta — zawęź filtry, żeby zobaczyć wszystkie klientki tego produktu."
          />
        )}

        {/* `layout="inline"` is flex-wrap without a row gap in antd v5, so the
            wrapped lines touch. The gap is what separates them. */}
        <Form<BulkFormModel> form={formController} layout="inline" onFinish={onSubmit} style={{ rowGap: 16 }}>
          {formValues.rewardType === "discount" && (
            <Form.Item label="Rabat" name="discountPct" rules={[{ required: true, type: "number", min: 1, max: 90 }]}>
              <InputNumber min={1} max={90} precision={0} addonAfter="%" />
            </Form.Item>
          )}
          <Form.Item label="Ważna do" name="expiresAt" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" disabledDate={(current) => current && current.isBefore(dayjs(), "day")} />
          </Form.Item>
          <Form.Item label="Wyślij maila" name="sendEmail" valuePropName="checked">
            <Switch />
          </Form.Item>
          <PayoffOfferCampaignFields values={formValues} />
        </Form>

        <Table<PayoffOfferCandidate>
          size="small"
          rowKey="productClientId"
          loading={loading}
          dataSource={filtered}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          rowSelection={{
            selectedRowKeys: effectiveSelection,
            onChange: (keys) => setSelectedIds(keys as string[]),
            getCheckboxProps: (candidate) => ({ disabled: !candidate.eligible }),
          }}
        >
          <Table.Column<PayoffOfferCandidate>
            title="Klientka"
            key="client"
            render={(_, candidate) => (
              <Space direction="vertical" size={0}>
                <span>
                  {candidate.firstName} {candidate.lastName}
                </span>
                <Typography.Text type="secondary">{candidate.email}</Typography.Text>
              </Space>
            )}
          />
          <Table.Column<PayoffOfferCandidate>
            title="Zakup"
            key="createdAt"
            render={(_, candidate) => (candidate.createdAt ? dayjs(candidate.createdAt).format("DD.MM.YYYY") : "—")}
          />
          <Table.Column<PayoffOfferCandidate>
            title="Raty"
            key="installments"
            render={(_, candidate) => (
              <Space direction="vertical" size={0}>
                <span>
                  {candidate.cycleNumber ?? "?"} / {candidate.installments ?? "?"}
                  {candidate.isManualPayment ? (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      przelew
                    </Tag>
                  ) : null}
                </span>
                <Typography.Text type="secondary">
                  {typeof candidate.remainingInstallments === "number"
                    ? `zostaje ${candidate.remainingInstallments}`
                    : "—"}
                </Typography.Text>
              </Space>
            )}
          />
          <Table.Column<PayoffOfferCandidate>
            title="Cena / wariant"
            key="price"
            render={(_, candidate) => (
              <Space direction="vertical" size={0}>
                <span>{candidate.priceDescription ?? candidate.priceId ?? "—"}</span>
                <Typography.Text type="secondary">{candidate.checkoutVariantSlug ?? "—"}</Typography.Text>
              </Space>
            )}
          />
          <Table.Column<PayoffOfferCandidate>
            title="Status"
            key="status"
            render={(_, candidate) => {
              if (!candidate.eligible) {
                return <Typography.Text type="secondary">{candidate.reason}</Typography.Text>;
              }

              return isOfferActive(candidate) ? (
                <Tooltip title={followUpLabel(candidate.currentOffer?.followUp)}>
                  <Tag color="gold">
                    ma ofertę {candidate.currentOffer ? rewardLabel(candidate.currentOffer) : ""} do{" "}
                    {dayjs(candidate.currentOffer?.expiresAt).format("DD.MM.YYYY")}
                  </Tag>
                </Tooltip>
              ) : (
                <Tag color="green">kwalifikuje się</Tag>
              );
            }}
          />
        </Table>

        {result && (
          <Alert
            type="info"
            showIcon
            message={`Przyznano ${result.granted.length}, pominięto ${result.skipped.length}, maili w kolejce: ${result.emailsQueued}, zaplanowanych przypomnień: ${result.followUpsScheduled}`}
            description={
              result.skipped.length ? (
                <ul className="m-0 pl-4">
                  {result.skipped.slice(0, 10).map((item) => (
                    <li key={item.productClientId}>
                      {candidates.find((candidate) => candidate.productClientId === item.productClientId)?.email ??
                        item.productClientId}
                      : {item.reason}
                    </li>
                  ))}
                  {result.skipped.length > 10 ? <li>…i {result.skipped.length - 10} więcej</li> : null}
                </ul>
              ) : null
            }
          />
        )}
      </Space>
    </Modal>
  );
};
